<template>
  <div class="auth-page">
    <div class="auth-bg"></div>
    <div class="auth-grid"></div>

    <div class="auth-container">
      <div class="auth-card">
        <!-- Header -->
        <div class="auth-header">
          <div class="auth-logo">
            <div class="auth-logo-icon">CS</div>
            <span class="auth-logo-text">Casino<span class="accent">Script</span></span>
          </div>
          <h1 class="auth-title">{{ $t('auth.register_title') }}</h1>
          <p class="auth-subtitle">{{ $t('auth.register_subtitle') }}</p>
        </div>

        <!-- Error Alert -->
        <div v-if="generalError" class="auth-error">
          <span class="auth-error-icon">!</span>
          <span>{{ generalError }}</span>
        </div>

        <!-- Form -->
        <form class="auth-form" @submit.prevent="handleSubmit">
          <!-- Username -->
          <div class="form-group">
            <label class="form-label" for="username">{{ $t('auth.username_label') }}</label>
            <div class="form-input-wrap">
              <svg class="form-icon" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd" />
              </svg>
              <input
                id="username"
                v-model="form.username"
                type="text"
                class="form-input"
                :class="fieldClass('username')"
                :placeholder="$t('auth.username_placeholder')"
                required
                autocomplete="username"
                @blur="validateField('username')"
              />
              <span v-if="fieldErrors.username" class="form-status form-status-error">
                <svg viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" /></svg>
              </span>
            </div>
            <p v-if="fieldErrors.username" class="form-error">{{ fieldErrors.username }}</p>
          </div>

          <!-- Email -->
          <div class="form-group">
            <label class="form-label" for="email">{{ $t('auth.email_label') }}</label>
            <div class="form-input-wrap">
              <svg class="form-icon" viewBox="0 0 20 20" fill="currentColor">
                <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
              </svg>
              <input
                id="email"
                v-model="form.email"
                type="email"
                class="form-input"
                :class="fieldClass('email')"
                :placeholder="$t('auth.email_placeholder')"
                required
                autocomplete="email"
                @blur="validateField('email')"
              />
              <span v-if="fieldErrors.email" class="form-status form-status-error">
                <svg viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" /></svg>
              </span>
            </div>
            <p v-if="fieldErrors.email" class="form-error">{{ fieldErrors.email }}</p>
          </div>

          <!-- Password -->
          <div class="form-group">
            <label class="form-label" for="password">{{ $t('auth.password_label') }}</label>
            <div class="form-input-wrap">
              <svg class="form-icon" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clip-rule="evenodd" />
              </svg>
              <input
                id="password"
                v-model="form.password"
                :type="showPassword ? 'text' : 'password'"
                class="form-input"
                :class="fieldClass('password')"
                :placeholder="$t('auth.password_min_chars')"
                required
                autocomplete="new-password"
                @blur="validateField('password')"
                @input="updatePasswordStrength"
              />
              <button
                type="button"
                class="form-input-toggle"
                @click="showPassword = !showPassword"
                tabindex="-1"
              >
                <svg v-if="!showPassword" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                  <path fill-rule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clip-rule="evenodd" />
                </svg>
                <svg v-else viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z" clip-rule="evenodd" />
                  <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z" />
                </svg>
              </button>
            </div>
            <p v-if="fieldErrors.password" class="form-error">{{ fieldErrors.password }}</p>
            <!-- Password Strength -->
            <div v-if="form.password" class="password-strength">
              <div class="password-strength-bar">
                <div
                  class="password-strength-fill"
                  :class="'strength-' + passwordStrength.level"
                  :style="{ width: passwordStrength.percent + '%' }"
                ></div>
              </div>
              <span class="password-strength-label" :class="'strength-' + passwordStrength.level">
                {{ passwordStrength.label }}
              </span>
            </div>
          </div>

          <!-- Confirm Password -->
          <div class="form-group">
            <label class="form-label" for="password_confirmation">{{ $t('auth.confirm_password_label') }}</label>
            <div class="form-input-wrap">
              <svg class="form-icon" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clip-rule="evenodd" />
              </svg>
              <input
                id="password_confirmation"
                v-model="form.password_confirmation"
                :type="showConfirmPassword ? 'text' : 'password'"
                class="form-input"
                :class="fieldClass('password_confirmation')"
                :placeholder="$t('auth.confirm_password_placeholder')"
                required
                autocomplete="new-password"
                @blur="validateField('password_confirmation')"
              />
              <button
                type="button"
                class="form-input-toggle"
                @click="showConfirmPassword = !showConfirmPassword"
                tabindex="-1"
              >
                <svg v-if="!showConfirmPassword" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                  <path fill-rule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clip-rule="evenodd" />
                </svg>
                <svg v-else viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z" clip-rule="evenodd" />
                  <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z" />
                </svg>
              </button>
            </div>
            <p v-if="fieldErrors.password_confirmation" class="form-error">{{ fieldErrors.password_confirmation }}</p>
          </div>

          <!-- Date of Birth & Country Row -->
          <div class="form-row">
            <div class="form-group">
              <label class="form-label" for="date_of_birth">{{ $t('auth.date_of_birth_label') }}</label>
              <div class="form-input-wrap">
                <svg class="form-icon" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clip-rule="evenodd" />
                </svg>
                <input
                  id="date_of_birth"
                  v-model="form.date_of_birth"
                  type="date"
                  class="form-input"
                  :class="fieldClass('date_of_birth')"
                  :max="maxDateOfBirth"
                  required
                  @blur="validateField('date_of_birth')"
                />
              </div>
              <p v-if="fieldErrors.date_of_birth" class="form-error">{{ fieldErrors.date_of_birth }}</p>
            </div>

            <div class="form-group">
              <label class="form-label" for="country">{{ $t('auth.country_label') }}</label>
              <div class="form-input-wrap">
                <svg class="form-icon" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM4.332 8.027a6.012 6.012 0 011.912-2.706C6.512 5.73 6.974 6 7.5 6A1.5 1.5 0 019 7.5V8a2 2 0 004 0 2 2 0 012 2v1a2 2 0 01-2 2 1 1 0 00-1 1v2.268a6.015 6.015 0 01-7.668-5.241z" clip-rule="evenodd" />
                </svg>
                <select
                  id="country"
                  v-model="form.country"
                  class="form-input form-select"
                  :class="fieldClass('country')"
                  required
                  @blur="validateField('country')"
                >
                  <option value="" disabled>{{ $t('auth.country_placeholder') }}</option>
                  <option v-for="c in countries" :key="c.code" :value="c.code">{{ c.name }}</option>
                </select>
              </div>
              <p v-if="fieldErrors.country" class="form-error">{{ fieldErrors.country }}</p>
            </div>
          </div>

          <!-- Referral Code (collapsible) -->
          <div class="form-group">
            <button type="button" class="referral-toggle" @click="showReferral = !showReferral">
              <svg class="referral-toggle-icon" :class="{ open: showReferral }" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
              </svg>
              {{ $t('auth.referral_code_question') }}
            </button>
            <div v-if="showReferral" class="referral-input-wrap">
              <div class="form-input-wrap">
                <svg class="form-icon" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M5 2a2 2 0 00-2 2v14l3.5-2 3.5 2 3.5-2 3.5 2V4a2 2 0 00-2-2H5zm2.5 3a1.5 1.5 0 100 3 1.5 1.5 0 000-3zm6.207.293a1 1 0 00-1.414 0l-6 6a1 1 0 101.414 1.414l6-6a1 1 0 000-1.414zM12.5 10a1.5 1.5 0 100 3 1.5 1.5 0 000-3z" clip-rule="evenodd" />
                </svg>
                <input
                  id="referral_code"
                  v-model="form.referral_code"
                  type="text"
                  class="form-input"
                  :placeholder="$t('auth.referral_code_placeholder')"
                />
              </div>
            </div>
          </div>

          <!-- Terms Checkbox -->
          <label class="checkbox-group">
            <input v-model="agreeTerms" type="checkbox" class="checkbox-hidden" />
            <span class="checkbox-custom" :class="{ checked: agreeTerms }">
              <svg v-if="agreeTerms" viewBox="0 0 12 12" fill="currentColor">
                <path d="M10.28 2.28a.75.75 0 00-1.06-1.06L4.5 5.94 2.78 4.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.06 0l5.25-5.25z" />
              </svg>
            </span>
            <span>{{ $t('auth.agree_to_terms_prefix') }} <a href="#" @click.prevent>{{ $t('auth.terms_link') }}</a> {{ $t('common.and') }} <a href="#" @click.prevent>{{ $t('auth.privacy_link') }}</a></span>
          </label>

          <!-- Age Checkbox -->
          <label class="checkbox-group">
            <input v-model="agreeAge" type="checkbox" class="checkbox-hidden" />
            <span class="checkbox-custom" :class="{ checked: agreeAge }">
              <svg v-if="agreeAge" viewBox="0 0 12 12" fill="currentColor">
                <path d="M10.28 2.28a.75.75 0 00-1.06-1.06L4.5 5.94 2.78 4.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.06 0l5.25-5.25z" />
              </svg>
            </span>
            <span>{{ $t('auth.age_confirm') }}</span>
          </label>
          <p v-if="checkboxError" class="form-error checkbox-error">{{ checkboxError }}</p>

          <!-- Sign Up Button -->
          <button type="submit" class="btn-signup" :disabled="isLoading">
            <span v-if="isLoading" class="btn-spinner"></span>
            <span v-else>{{ $t('auth.create_account') }}</span>
          </button>
        </form>

        <!-- Footer -->
        <div class="auth-footer">
          {{ $t('auth.have_account') }} <router-link to="/login">{{ $t('auth.sign_in_here') }}</router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useUserStore } from '@/stores/user'

const router = useRouter()
const { t } = useI18n()
const userStore = useUserStore()

const form = reactive({
  username: '',
  email: '',
  password: '',
  password_confirmation: '',
  date_of_birth: '',
  country: '',
  referral_code: '',
})

const showPassword = ref(false)
const showConfirmPassword = ref(false)
const showReferral = ref(false)
const agreeTerms = ref(false)
const agreeAge = ref(false)
const isLoading = ref(false)
const generalError = ref('')
const checkboxError = ref('')

const fieldErrors = reactive<Record<string, string>>({
  username: '',
  email: '',
  password: '',
  password_confirmation: '',
  date_of_birth: '',
  country: '',
})

const touched = reactive<Record<string, boolean>>({})

// 18 years ago from today
const maxDateOfBirth = computed(() => {
  const d = new Date()
  d.setFullYear(d.getFullYear() - 18)
  return d.toISOString().split('T')[0]
})

const countries = [
  { code: 'US', name: 'United States' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'CA', name: 'Canada' },
  { code: 'AU', name: 'Australia' },
  { code: 'DE', name: 'Germany' },
  { code: 'FR', name: 'France' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'SE', name: 'Sweden' },
  { code: 'NO', name: 'Norway' },
  { code: 'FI', name: 'Finland' },
  { code: 'DK', name: 'Denmark' },
  { code: 'IE', name: 'Ireland' },
  { code: 'NZ', name: 'New Zealand' },
  { code: 'AT', name: 'Austria' },
  { code: 'CH', name: 'Switzerland' },
  { code: 'BE', name: 'Belgium' },
  { code: 'PT', name: 'Portugal' },
  { code: 'ES', name: 'Spain' },
  { code: 'IT', name: 'Italy' },
  { code: 'PL', name: 'Poland' },
  { code: 'CZ', name: 'Czech Republic' },
  { code: 'BR', name: 'Brazil' },
  { code: 'MX', name: 'Mexico' },
  { code: 'AR', name: 'Argentina' },
  { code: 'CL', name: 'Chile' },
  { code: 'JP', name: 'Japan' },
  { code: 'KR', name: 'South Korea' },
  { code: 'SG', name: 'Singapore' },
  { code: 'IN', name: 'India' },
  { code: 'ZA', name: 'South Africa' },
]

// Password strength
const passwordStrength = computed(() => {
  const pw = form.password
  if (!pw) return { level: 0, percent: 0, label: '' }

  let score = 0
  if (pw.length >= 8) score++
  if (pw.length >= 12) score++
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) score++
  if (/\d/.test(pw)) score++
  if (/[^a-zA-Z0-9]/.test(pw)) score++

  if (score <= 1) return { level: 1, percent: 20, label: t('auth.password_strength_weak') }
  if (score === 2) return { level: 2, percent: 40, label: t('auth.password_strength_fair') }
  if (score === 3) return { level: 3, percent: 60, label: t('auth.password_strength_good') }
  if (score === 4) return { level: 4, percent: 80, label: t('auth.password_strength_strong') }
  return { level: 5, percent: 100, label: t('auth.password_strength_very_strong') }
})

function updatePasswordStrength() {
  // Also revalidate confirmation if already touched
  if (touched.password_confirmation && form.password_confirmation) {
    validateField('password_confirmation')
  }
}

function validateField(field: string) {
  touched[field] = true
  fieldErrors[field] = ''

  switch (field) {
    case 'username': {
      const v = form.username.trim()
      if (!v) { fieldErrors.username = t('auth.register_error_username_required'); break }
      if (v.length < 3) { fieldErrors.username = t('auth.register_error_username_min'); break }
      if (v.length > 20) { fieldErrors.username = t('auth.register_error_username_max'); break }
      if (!/^[a-zA-Z0-9_-]+$/.test(v)) { fieldErrors.username = t('auth.register_error_username_format'); break }
      break
    }
    case 'email': {
      const v = form.email.trim()
      if (!v) { fieldErrors.email = t('auth.register_error_email_required'); break }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) { fieldErrors.email = t('auth.register_error_email_invalid'); break }
      break
    }
    case 'password': {
      if (!form.password) { fieldErrors.password = t('auth.register_error_password_required'); break }
      if (form.password.length < 8) { fieldErrors.password = t('auth.register_error_password_min'); break }
      break
    }
    case 'password_confirmation': {
      if (!form.password_confirmation) { fieldErrors.password_confirmation = t('auth.register_error_confirm_required'); break }
      if (form.password_confirmation !== form.password) { fieldErrors.password_confirmation = t('auth.register_error_confirm_match'); break }
      break
    }
    case 'date_of_birth': {
      if (!form.date_of_birth) { fieldErrors.date_of_birth = t('auth.register_error_dob_required'); break }
      if (form.date_of_birth > maxDateOfBirth.value) { fieldErrors.date_of_birth = t('auth.register_error_min_age'); break }
      break
    }
    case 'country': {
      if (!form.country) { fieldErrors.country = t('auth.register_error_country_required'); break }
      break
    }
  }
}

function fieldClass(field: string) {
  if (!touched[field]) return ''
  return fieldErrors[field] ? 'invalid' : 'valid'
}

function validateAll(): boolean {
  const fields = ['username', 'email', 'password', 'password_confirmation', 'date_of_birth', 'country'] as const
  fields.forEach(f => validateField(f))

  checkboxError.value = ''
  if (!agreeTerms.value || !agreeAge.value) {
    checkboxError.value = t('auth.register_error_terms_required')
  }

  return !fields.some(f => fieldErrors[f]) && !checkboxError.value
}

function applyServerErrors(errors: Record<string, string[]>) {
  for (const [field, messages] of Object.entries(errors)) {
    if (field in fieldErrors) {
      fieldErrors[field] = messages[0]
      touched[field] = true
    }
  }
}

async function handleSubmit() {
  generalError.value = ''
  checkboxError.value = ''

  if (!validateAll()) return

  isLoading.value = true

  try {
    await userStore.register({
      username: form.username.trim(),
      email: form.email.trim(),
      password: form.password,
      password_confirmation: form.password_confirmation,
      date_of_birth: form.date_of_birth,
      country: form.country,
      preferred_currency: 'USD',
      referral_code: form.referral_code.trim() || undefined,
    })

    router.push('/verify-email')
  } catch (e: unknown) {
    const axiosError = e as { response?: { data?: { message?: string; errors?: Record<string, string[]> }; status?: number } }
    const status = axiosError.response?.status

    if (status === 422 && axiosError.response?.data?.errors) {
      applyServerErrors(axiosError.response.data.errors)
    } else if (status === 429) {
      generalError.value = t('auth.register_error_rate_limited')
    } else {
      generalError.value = axiosError.response?.data?.message || t('auth.error_generic')
    }
  } finally {
    isLoading.value = false
  }
}
</script>

<style scoped>
.auth-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-primary);
  overflow: hidden;
  position: relative;
}

.auth-bg {
  position: fixed;
  inset: 0;
  z-index: 0;
  background-image:
    radial-gradient(ellipse at 20% 50%, rgba(0, 231, 1, 0.05) 0%, transparent 50%),
    radial-gradient(ellipse at 80% 20%, rgba(20, 117, 225, 0.04) 0%, transparent 50%);
  pointer-events: none;
}

.auth-grid {
  position: fixed;
  inset: 0;
  background-image:
    linear-gradient(rgba(0, 231, 1, 0.02) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0, 231, 1, 0.02) 1px, transparent 1px);
  background-size: 60px 60px;
  pointer-events: none;
  z-index: 0;
}

.auth-container {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 460px;
  padding: var(--space-5);
  max-height: 100vh;
  overflow-y: auto;
}

.auth-card {
  background: linear-gradient(135deg, rgba(26, 44, 56, 0.95) 0%, rgba(33, 55, 67, 0.85) 100%);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: var(--radius-xl);
  padding: var(--space-10);
  backdrop-filter: blur(20px);
  box-shadow: var(--shadow-lg);
  animation: slideUp 0.6s ease both;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Header */
.auth-header {
  text-align: center;
  margin-bottom: var(--space-8);
}

.auth-logo {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  margin-bottom: var(--space-5);
}

.auth-logo-icon {
  width: 40px;
  height: 40px;
  background: var(--accent-primary);
  border-radius: var(--radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #000;
  font-size: 14px;
  font-weight: 700;
  font-family: var(--font-family-display);
  box-shadow: var(--shadow-glow-green);
}

.auth-logo-text {
  font-family: var(--font-family-display);
  font-size: 18px;
  font-weight: 700;
  color: var(--text-primary);
}

.auth-logo-text .accent {
  color: var(--accent-primary);
}

.auth-title {
  font-size: var(--font-size-h1);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  margin-bottom: var(--space-2);
}

.auth-subtitle {
  font-size: var(--font-size-body);
  color: var(--text-tertiary);
}

/* Error Alert */
.auth-error {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-3) var(--space-4);
  background: var(--color-danger-bg);
  border: 1px solid var(--color-danger-border);
  border-radius: var(--radius-md);
  color: var(--color-danger-light);
  font-size: var(--font-size-body);
  margin-bottom: var(--space-4);
}

.auth-error-icon {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border-radius: var(--radius-full);
  background: var(--color-danger);
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Form */
.auth-form {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  margin-bottom: var(--space-6);
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-3);
}

.form-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-secondary);
}

.form-input-wrap {
  position: relative;
  display: flex;
  align-items: center;
}

.form-icon {
  position: absolute;
  left: 12px;
  width: 16px;
  height: 16px;
  color: var(--text-tertiary);
  pointer-events: none;
}

.form-input {
  width: 100%;
  background: rgba(15, 25, 35, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: var(--radius-md);
  padding: 12px 16px;
  padding-left: 40px;
  color: var(--text-primary);
  font-size: var(--font-size-body);
  font-family: var(--font-family-primary);
  transition: all var(--transition-fast);
}

.form-input:focus {
  outline: none;
  border-color: rgba(20, 117, 225, 0.5);
  background: rgba(15, 25, 35, 1);
}

.form-input::placeholder {
  color: var(--text-tertiary);
}

.form-input.valid {
  border-color: rgba(0, 231, 1, 0.3);
}

.form-input.invalid {
  border-color: rgba(237, 66, 69, 0.3);
}

.form-select {
  appearance: none;
  cursor: pointer;
  padding-right: 36px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='%237F8FA4' viewBox='0 0 20 20'%3E%3Cpath d='M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 16px;
}

.form-select option {
  background: var(--bg-secondary);
  color: var(--text-primary);
}

.form-input-toggle {
  position: absolute;
  right: 12px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: var(--text-tertiary);
  transition: color var(--transition-fast);
  display: flex;
  align-items: center;
}

.form-input-toggle:hover {
  color: var(--text-secondary);
}

.form-input-toggle svg {
  width: 18px;
  height: 18px;
}

.form-status {
  position: absolute;
  right: 12px;
  display: flex;
  align-items: center;
}

.form-status svg {
  width: 16px;
  height: 16px;
}

.form-status-error {
  color: var(--color-danger);
}

.form-error {
  font-size: var(--font-size-sm);
  color: var(--color-danger);
  margin: 0;
}

.checkbox-error {
  margin-top: calc(-1 * var(--space-2));
}

/* Password Strength */
.password-strength {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.password-strength-bar {
  flex: 1;
  height: 4px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: var(--radius-full);
  overflow: hidden;
}

.password-strength-fill {
  height: 100%;
  border-radius: var(--radius-full);
  transition: width var(--transition-normal), background var(--transition-normal);
}

.password-strength-fill.strength-1 { background: var(--color-danger); }
.password-strength-fill.strength-2 { background: var(--color-warning); }
.password-strength-fill.strength-3 { background: var(--color-warning); }
.password-strength-fill.strength-4 { background: var(--color-success); }
.password-strength-fill.strength-5 { background: var(--color-success); }

.password-strength-label {
  font-size: var(--font-size-sm);
  white-space: nowrap;
}

.password-strength-label.strength-1 { color: var(--color-danger); }
.password-strength-label.strength-2 { color: var(--color-warning); }
.password-strength-label.strength-3 { color: var(--color-warning); }
.password-strength-label.strength-4 { color: var(--color-success); }
.password-strength-label.strength-5 { color: var(--color-success); }

/* Referral Toggle */
.referral-toggle {
  display: flex;
  align-items: center;
  gap: var(--space-1);
  font-size: var(--font-size-body);
  color: var(--accent-secondary);
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  transition: color var(--transition-fast);
}

.referral-toggle:hover {
  color: var(--text-primary);
}

.referral-toggle-icon {
  width: 16px;
  height: 16px;
  transition: transform var(--transition-fast);
}

.referral-toggle-icon.open {
  transform: rotate(180deg);
}

.referral-input-wrap {
  margin-top: var(--space-2);
}

/* Checkboxes */
.checkbox-group {
  display: flex;
  align-items: flex-start;
  gap: var(--space-2);
  font-size: 13px;
  color: var(--text-secondary);
  cursor: pointer;
}

.checkbox-group a {
  color: var(--accent-secondary);
  text-decoration: none;
  transition: all var(--transition-fast);
}

.checkbox-group a:hover {
  text-decoration: underline;
}

.checkbox-hidden {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.checkbox-custom {
  width: 18px;
  height: 18px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: var(--radius-sm, 4px);
  background: rgba(255, 255, 255, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 2px;
  transition: all var(--transition-fast);
}

.checkbox-custom:hover {
  border-color: rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.08);
}

.checkbox-custom.checked {
  background: var(--accent-primary);
  border-color: var(--accent-primary);
}

.checkbox-custom svg {
  width: 12px;
  height: 12px;
  color: #000;
}

/* Sign Up Button */
.btn-signup {
  width: 100%;
  padding: 12px 16px;
  background: var(--accent-primary);
  color: var(--color-on-accent);
  font-size: var(--font-size-body);
  font-weight: var(--font-weight-bold);
  font-family: var(--font-family-primary);
  border-radius: var(--radius-md);
  border: none;
  cursor: pointer;
  transition: all var(--transition-fast);
  letter-spacing: 0.02em;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  min-height: 44px;
}

.btn-signup:hover:not(:disabled) {
  background: var(--accent-primary-hover);
  box-shadow: var(--shadow-glow-green);
  transform: translateY(-2px);
}

.btn-signup:active:not(:disabled) {
  transform: translateY(0);
}

.btn-signup:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.btn-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(0, 0, 0, 0.2);
  border-top-color: #000;
  border-radius: var(--radius-full);
  animation: spin 0.6s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Footer */
.auth-footer {
  text-align: center;
  font-size: 13px;
  color: var(--text-tertiary);
}

.auth-footer a {
  color: var(--accent-secondary);
  text-decoration: none;
  transition: all var(--transition-fast);
}

.auth-footer a:hover {
  color: var(--text-primary);
  text-decoration: underline;
}

/* Responsive */
@media (max-width: 480px) {
  .auth-card {
    padding: 28px;
  }

  .auth-title {
    font-size: 20px;
  }

  .form-row {
    grid-template-columns: 1fr;
  }
}
</style>
